import React, { useState } from 'react'
import { Alert } from 'react-bootstrap'
import { AccountCustomWorkflowRunSummary } from '../../../store/customWorkflow/customWorkflows'
import { Spinner } from '../../../components/Spinner/Spinner'
import { RunRowAction, WorkflowRunRow } from './WorkflowRunRow'
import { ChildRunsList } from './ChildRunsList'
import { RunsPager } from './RunsPager'
import { isRunFinished } from '../../../components/CustomWorkflows/runStatus'

interface RunsTabProps {
  accountId: string
  runs: AccountCustomWorkflowRunSummary[]
  isLoading: boolean
  isError: boolean
  page: number
  totalPages: number
  onPageChange: (page: number) => void
  emptyMessage: string
  actionFor?: (run: AccountCustomWorkflowRunSummary) => RunRowAction | undefined
  showSubRunBadge?: boolean
  groupChildRuns?: boolean
}

export const RunsTab: React.FC<RunsTabProps> = ({
  accountId,
  runs,
  isLoading,
  isError,
  page,
  totalPages,
  onPageChange,
  emptyMessage,
  actionFor,
  showSubRunBadge,
  groupChildRuns,
}) => {
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const toggle = (runId: string) =>
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(runId)) {
        next.delete(runId)
      } else {
        next.add(runId)
      }
      return next
    })

  if (isLoading) {
    return (
      <div className='d-flex justify-content-center py-5'>
        <Spinner />
      </div>
    )
  }
  if (isError) {
    return <Alert variant='warning'>Could not load your workflow runs.</Alert>
  }
  if (!runs.length) {
    return <div className='py-5 text-center zocks-text-gray-600'>{emptyMessage}</div>
  }
  return (
    <div className='d-flex flex-column zocks-gap-2'>
      {runs.map((run) => (
        <React.Fragment key={run.id}>
          <WorkflowRunRow
            run={run}
            action={actionFor?.(run)}
            showSubRunBadge={showSubRunBadge}
            expanded={expanded.has(run.id)}
            onToggleExpand={groupChildRuns && (run.childCount ?? 0) > 0 ? () => toggle(run.id) : undefined}
          />
          {groupChildRuns && expanded.has(run.id) ? (
            <ChildRunsList accountId={accountId} parentRunId={run.id} parentActive={!isRunFinished(run.status)} actionFor={actionFor} />
          ) : null}
        </React.Fragment>
      ))}
      <div className='d-flex justify-content-end pt-2'>
        <RunsPager page={page} totalPages={totalPages} onPageChange={onPageChange} />
      </div>
    </div>
  )
}
